import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, platformStyle, displayPlatforms } from "../../lib/api";
import { PublicNav, PublicFooter } from "../../components/public/PublicNav";
import { useSeo } from "../../hooks/useSeo";
import { useReleaseModal } from "../../components/public/ReleaseModal";
import { Trophy, Medal, ThumbsUp } from "lucide-react";

export const PublicTop = () => {
  useSeo({
    title: "Top du mois — Films & séries plébiscités | Rouge Première",
    description: "Le classement du mois : les 3 films et 3 séries les plus votés par les visiteurs sur Rouge Première.",
  });
  const [films, setFilms] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/top", { params: { kind: "Film", limit: 3 } }),
      api.get("/top", { params: { kind: "Série", limit: 3 } }),
    ])
      .then(([f, s]) => {
        setFilms(f.data);
        setSeries(s.data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PublicNav />
      <main className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20" data-testid="top-page">
        <header className="mb-14 md:mb-20">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] flex items-center gap-2">
            <Trophy className="h-4 w-4" /> Top du mois
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Les préférés des visiteurs</h1>
          <p className="mt-4 text-neutral-600 max-w-2xl leading-relaxed">
            Chaque mois, un classement des trois films et trois séries qui ont récolté le plus de
            pouces levés parmi celles déjà disponibles en salle ou en streaming.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8">
          <TopColumn label="Films" items={films} loading={loading} testId="top-films" />
          <TopColumn label="Séries" items={series} loading={loading} testId="top-series" />
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

const TopColumn = ({ label, items, loading, testId }) => {
  const { openRelease } = useReleaseModal();
  return (
  <section
    data-testid={testId}
    className="rounded-lg border border-[var(--line)] bg-white p-6 md:p-8"
  >
    <div className="flex items-center gap-2 pb-5 mb-6 border-b border-[var(--line)]">
      <Trophy className="h-5 w-5 text-[var(--red)]" />
      <h2 className="font-display text-2xl md:text-3xl">Top 3 {label}</h2>
    </div>
    {loading ? (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-4 items-center animate-pulse">
            <div className="h-24 w-16 bg-neutral-100 rounded-sm" />
            <div className="flex-1 h-4 bg-neutral-100 rounded" />
          </div>
        ))}
      </div>
    ) : items.length === 0 ? (
      <div className="py-10 text-center text-sm text-neutral-500">
        Le classement se construit à mesure des votes. Revenez d'ici quelques jours.
      </div>
    ) : (
      <ol className="space-y-5">
        {items.map((r, i) => (
          <li
            key={`${r.type}-${r.tmdb_id}`}
            role="button"
            tabIndex={0}
            data-testid={`top-item-${r.type}-${r.tmdb_id}`}
            aria-label={`Voir le détail de ${r.title}`}
            onClick={() => openRelease(r)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openRelease(r))}
            className="flex gap-4 items-center cursor-pointer group rounded-sm hover:bg-[var(--bg-cream)] transition-colors -mx-2 px-2 py-1"
          >
            <RankBadge n={i + 1} />
            {r.poster_url ? (
              <img
                src={r.poster_url}
                alt={r.title}
                className="h-24 w-16 object-cover rounded-sm shrink-0"
              />
            ) : (
              <div className="h-24 w-16 bg-neutral-100 rounded-sm shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg leading-tight group-hover:text-[var(--red)] transition-colors">{r.title}</div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">
                {(r.genres || []).slice(0, 3).join(" · ") || "—"}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {displayPlatforms(r).slice(0, 2).map((p) => (
                  <span
                    key={p}
                    className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm ${
                      platformStyle[p] || platformStyle["À venir"]
                    }`}
                  >
                    {p}
                  </span>
                ))}
                {r.release_date && (
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                    {fmtDate(r.release_date)}
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                <ThumbsUp className="h-4 w-4" />
                <span className="tabular-nums">{r._month_up ?? 0}</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-0.5">
                ce mois
              </div>
            </div>
          </li>
        ))}
      </ol>
    )}
    <div className="mt-6 pt-5 border-t border-[var(--line)] text-right">
      <Link
        to={`/sorties?kind=${encodeURIComponent(label === "Films" ? "Film" : "Série")}`}
        className="text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]"
      >
        Voir toutes les sorties →
      </Link>
    </div>
  </section>
  );
};

const RankBadge = ({ n }) => {
  const cls =
    n === 1
      ? "bg-[var(--red)] text-white"
      : n === 2
      ? "bg-neutral-900 text-white"
      : "bg-neutral-200 text-neutral-800";
  return (
    <span
      className={`grid place-items-center h-10 w-10 rounded-full font-display shrink-0 ${cls}`}
    >
      <Medal className="h-4 w-4" />
    </span>
  );
};
